import { deepGet } from '@app/core';
import { TableColumn } from '@app/shared';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortConfig {
  column: TableColumn;
  direction: SortDirection;
}

// Orden con el que abre una tabla. columnName es el pathData.name de la columna.
export interface DefaultSort {
  columnName: string;
  direction: 'asc' | 'desc';
}

// Función para comparar valores nulos/indefinidos
function compareNullValues(a: any, b: any): number {
  const aIsNull = a === null || a === undefined || a === '';
  const bIsNull = b === null || b === undefined || b === '';

  if (aIsNull && bIsNull) return 0;
  if (aIsNull) return 1; // Los valores nulos van al final
  if (bIsNull) return -1;

  return 0; // Ninguno es nulo, continuar con comparación normal
}

// Función para comparar strings
function compareStrings(a: string, b: string): number {
  const aStr = String(a || '').toLowerCase().trim();
  const bStr = String(b || '').toLowerCase().trim();
  return aStr.localeCompare(bStr);
}

// Función para comparar números
function compareNumbers(a: number, b: number): number {
  const aNum = Number(a) || 0;
  const bNum = Number(b) || 0;
  return aNum - bNum;
}

// Función para comparar fechas
function compareDates(a: any, b: any): number {
  const aDate = a instanceof Date ? a : new Date(a);
  const bDate = b instanceof Date ? b : new Date(b);

  if (isNaN(aDate.getTime()) && isNaN(bDate.getTime())) return 0;
  if (isNaN(aDate.getTime())) return 1;
  if (isNaN(bDate.getTime())) return -1;

  return aDate.getTime() - bDate.getTime();
}

// Función para comparar booleanos
function compareBooleans(a: boolean, b: boolean): number {
  const aBool = Boolean(a);
  const bBool = Boolean(b);
  return Number(aBool) - Number(bBool);
}

// Función para obtener el valor de ordenamiento de un item según el tipo de columna
function getSortValue<T>(item: T, column: TableColumn): any {
  if (!column.pathData) return null;

  if (column.sortValue) return column.sortValue(item);

  // Para columnas de tipo select y avatar-select (propiedades anidadas)
  if ((column.pathData.type === 'select' || column.pathData.type === 'avatar-select') && 
      column.pathData.nestedPath && column.optionList) {
    const entityId = (item as Record<string, any>)[column.pathData.name];
    const relatedEntity = column.optionList.find(opt => opt.id === entityId);

    if (relatedEntity && column.pathData.nestedPath) {
      return deepGet(relatedEntity, column.pathData.nestedPath);
    }
    return null;
  }

  // Para columnas básicas, usar directamente el pathData.name
  return deepGet(item, column.pathData.name);
}

// Función principal de ordenamiento
export function sortData<T>(data: T[], config: SortConfig): T[] {
  if (!config.direction || !config.column.pathData) {
    return [...data];
  }

  const sortedData = [...data].sort((a, b) => {
    const valueA = getSortValue(a, config.column);
    const valueB = getSortValue(b, config.column);

    // Manejar valores nulos/indefinidos
    const nullComparison = compareNullValues(valueA, valueB);
    if (nullComparison !== 0) {
      return config.direction === 'asc' ? nullComparison : -nullComparison;
    }

    // Determinar el tipo de dato y aplicar la comparación correspondiente
    let comparison = 0;

    if (typeof valueA === 'string' || typeof valueB === 'string') {
      comparison = compareStrings(valueA, valueB);
    } else if (typeof valueA === 'number' || typeof valueB === 'number') {
      comparison = compareNumbers(valueA, valueB);
    } else if (valueA instanceof Date || valueB instanceof Date ||
               (typeof valueA === 'string' && !isNaN(Date.parse(valueA))) ||
               (typeof valueB === 'string' && !isNaN(Date.parse(valueB)))) {
      comparison = compareDates(valueA, valueB);
    } else if (typeof valueA === 'boolean' || typeof valueB === 'boolean') {
      comparison = compareBooleans(valueA, valueB);
    } else {
      // Fallback: convertir a string y comparar
      comparison = compareStrings(String(valueA), String(valueB));
    }

    return config.direction === 'asc' ? comparison : -comparison;
  });

  return sortedData;
}

// Función para obtener el siguiente estado de ordenamiento
export function getNextSortDirection(currentDirection: SortDirection): SortDirection {
  switch (currentDirection) {
    case null:
      return 'asc';
    case 'asc':
      return 'desc';
    case 'desc':
      return null;
    default:
      return 'asc';
  }
}

// Función para crear una función de ordenamiento específica para una columna
export function createSortFunction<T>(
  data: T[],
  column: TableColumn,
  onSortChange: (sortedData: T[]) => void,
  onDirectionChange: (direction: SortDirection) => void
): () => void {
  return () => {
    const currentDirection = getNextSortDirection(null); // Siempre empieza en asc
    const sortedData = sortData(data, { column, direction: currentDirection });

    onDirectionChange(currentDirection);
    onSortChange(sortedData);
  };
}
